import { lazy, Suspense, useEffect, useState } from 'react';
import { UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';
import { RecoveryHost } from '../ui/RecoveryHost';

// Event-driven dialogs fetch their modules on first open and stay mounted for
// the session afterwards. The unsaved-changes guard and the recovery host stay
// eager on purpose: both must observe store/bridge events from boot.
const NewProjectDialog = lazy(() =>
    import('../ui/NewProjectDialog').then(m => ({ default: m.NewProjectDialog })));
const SaveTemplateDialog = lazy(() =>
    import('../ui/SaveTemplateDialog').then(m => ({ default: m.SaveTemplateDialog })));
const FileBrowserDialog = lazy(() =>
    import('../ui/FileBrowserDialog').then(m => ({ default: m.FileBrowserDialog })));

/**
 * Project Dialogs Host
 *
 * Mounts the document-level dialogs once for the whole application and
 * opens them via window events, so any command, tool or menu can trigger
 * them without prop drilling:
 * - 'yap:open-new-project-dialog'  → New Project (template gallery)
 * - 'yap:open-save-template-dialog' → Save Project as Template
 * - 'yap:open-file-browser'        → in-app file browser (fallback FS UI)
 * The unsaved-changes guard is store-driven (useUnsavedChangesStore), so it
 * simply mounts here too.
 */
export function ProjectDialogsHost() {
    const [newProjectOpen, setNewProjectOpen] = useState(false);
    const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
    const [browserMode, setBrowserMode] = useState<'open' | 'save' | null>(null);
    // Mount-once gates: once a dialog has been opened its module stays
    // mounted, so internal state survives open/close cycles as before.
    const [newProjectMounted, setNewProjectMounted] = useState(false);
    const [saveTemplateMounted, setSaveTemplateMounted] = useState(false);
    const [browserMounted, setBrowserMounted] = useState(false);

    useEffect(() => {
        const handleNewProject = () => {
            setNewProjectMounted(true);
            setNewProjectOpen(true);
        };
        const handleSaveTemplate = () => {
            setSaveTemplateMounted(true);
            setSaveTemplateOpen(true);
        };
        const handleFileBrowser = (e: Event) => {
            setBrowserMounted(true);
            setBrowserMode((e as CustomEvent).detail?.mode === 'save' ? 'save' : 'open');
        };

        window.addEventListener('yap:open-new-project-dialog', handleNewProject);
        window.addEventListener('yap:open-save-template-dialog', handleSaveTemplate);
        window.addEventListener('yap:open-file-browser', handleFileBrowser);
        return () => {
            window.removeEventListener('yap:open-new-project-dialog', handleNewProject);
            window.removeEventListener('yap:open-save-template-dialog', handleSaveTemplate);
            window.removeEventListener('yap:open-file-browser', handleFileBrowser);
        };
    }, []);

    return (
        <>
            {newProjectMounted && (
                <Suspense fallback={null}>
                    <NewProjectDialog isOpen={newProjectOpen} onClose={() => setNewProjectOpen(false)} />
                </Suspense>
            )}
            {saveTemplateMounted && (
                <Suspense fallback={null}>
                    <SaveTemplateDialog isOpen={saveTemplateOpen} onClose={() => setSaveTemplateOpen(false)} />
                </Suspense>
            )}
            {browserMounted && (
                <Suspense fallback={null}>
                    <FileBrowserDialog
                        isOpen={browserMode !== null}
                        mode={browserMode ?? 'open'}
                        onClose={() => setBrowserMode(null)}
                    />
                </Suspense>
            )}
            <UnsavedChangesDialog />
            <RecoveryHost />
        </>
    );
}
